import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  AssistantItemRow,
  AssistantOverview,
  AssistantRunLog,
  AssistantScheduleRow,
  AssistantThreadMessages,
  HttpResponse,
} from "@/types";

// Reads for the admin Assistant page (guac `admin/assistant`). Admin and
// product users only; guac rejects anyone else.

// guacHttpClient's base URL already ends in /api/admin.
const BASE = "assistant";
const REFRESH_MS = 60 * 1000;

const query = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
};

export const assistantAdminQueryKey = ["admin-assistant"] as const;

export function useGetAssistantOverview({ days }: { days: number }) {
  return useQuery({
    queryKey: [...assistantAdminQueryKey, "overview", days],
    queryFn: async () => {
      const res = await guacHttpClient.get<
        AssistantOverview,
        HttpResponse<AssistantOverview>
      >(`${BASE}/overview${query({ days })}`);
      return res.data;
    },
    refetchInterval: REFRESH_MS,
  });
}

export function useGetAssistantThreadMessages({
  threadId,
}: {
  threadId: string | undefined;
}) {
  return useInfiniteQuery({
    queryKey: [...assistantAdminQueryKey, "messages", threadId],
    enabled: !!threadId,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const res = await guacHttpClient.get<
        AssistantThreadMessages,
        HttpResponse<AssistantThreadMessages>
      >(
        `${BASE}/threads/${threadId ?? ""}/messages${query({ before: pageParam, limit: 100 })}`,
      );
      return res.data;
    },
    // Pages go back in time; the oldest message of a page is the next cursor.
    getNextPageParam: (page) =>
      page.hasMore ? page.messages[0]?.id : undefined,
    refetchInterval: REFRESH_MS,
  });
}

export function useGetAssistantItems({
  days,
  status,
  stafferUserId,
}: {
  days: number;
  status?: string;
  stafferUserId?: number;
}) {
  return useQuery({
    queryKey: [...assistantAdminQueryKey, "items", days, status, stafferUserId],
    queryFn: async () => {
      const res = await guacHttpClient.get<
        AssistantItemRow[],
        HttpResponse<AssistantItemRow[]>
      >(`${BASE}/items${query({ days, status, stafferUserId })}`);
      return res.data;
    },
    refetchInterval: REFRESH_MS,
  });
}

export function useGetAssistantSchedules({
  status,
  source,
}: {
  status?: string;
  source?: string;
}) {
  return useQuery({
    queryKey: [...assistantAdminQueryKey, "schedules", status, source],
    queryFn: async () => {
      const res = await guacHttpClient.get<
        AssistantScheduleRow[],
        HttpResponse<AssistantScheduleRow[]>
      >(`${BASE}/schedules${query({ status, source })}`);
      return res.data;
    },
    refetchInterval: REFRESH_MS,
  });
}

export function useGetAssistantRuns({ kind }: { kind?: string }) {
  return useQuery({
    queryKey: [...assistantAdminQueryKey, "runs", kind],
    queryFn: async () => {
      const res = await guacHttpClient.get<
        AssistantRunLog[],
        HttpResponse<AssistantRunLog[]>
      >(`${BASE}/runs${query({ kind, limit: 200 })}`);
      return res.data;
    },
    refetchInterval: REFRESH_MS,
  });
}
